import { useLDClient } from 'launchdarkly-react-client-sdk';
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import FeatureFlagsProviderContext from './FeatureFlagsProviderContext';

interface FeatureFlagsResolverProps {
  async?: boolean;
}

type PendingContext = { name: string; value: unknown };

const FeatureFlagsResolver = ({
  async,
  children,
}: PropsWithChildren<FeatureFlagsResolverProps>): JSX.Element => {
  const ldClient = useLDClient();
  const [ready, setReady] = useState(!!async);
  const [isUserContextIdentified, setUserContextIdentified] = useState(false);
  const identifyGenerationRef = useRef(0);
  /** Latest valid `setContext` args; kept until consumed by `identify` (cleared when a flush starts, not in the callback). */
  const pendingContextRef = useRef<PendingContext | null>(null);

  const flushIdentifyIfPossible = useCallback(() => {
    if (!ldClient) {
      return;
    }
    const pending = pendingContextRef.current;
    if (!pending?.name || !pending.value) {
      return;
    }

    const { name, value } = pending;
    // Consume pending now so Strict Mode double-effects and overlapping flushes only run one identify per snapshot.
    pendingContextRef.current = null;

    const context = {
      kind: 'multi' as const,
      [name]: value,
    };

    identifyGenerationRef.current += 1;
    const generation = identifyGenerationRef.current;
    setUserContextIdentified(false);

    ldClient.identify(context, undefined, () => {
      if (identifyGenerationRef.current !== generation) {
        return;
      }
      setReady(true);
      setUserContextIdentified(true);
    });
  }, [ldClient]);

  const setContext = useCallback(
    (name: string, value: unknown) => {
      if (!name || !value) {
        return;
      }

      pendingContextRef.current = { name, value };

      if (!ldClient) {
        setUserContextIdentified(false);
        return;
      }

      flushIdentifyIfPossible();
    },
    [ldClient, flushIdentifyIfPossible]
  );

  // When the LD client appears after valid `setContext` calls, run the same identify path as `setContext` would.
  useEffect(() => {
    if (!ldClient) {
      return;
    }
    flushIdentifyIfPossible();
  }, [ldClient, flushIdentifyIfPossible]);

  return (
    <FeatureFlagsProviderContext.Provider
      value={{ ready, isUserContextIdentified, setContext }}
    >
      {children}
    </FeatureFlagsProviderContext.Provider>
  );
};

export default FeatureFlagsResolver;
